import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  OverlayTrigger,
  ProgressBar,
  Row,
  Tooltip,
} from 'react-bootstrap';
import type { DppCompletenessCheck } from './dppCompleteness';
import type {
  CareSymbolCategory,
  ProductForm,
  UpdateProductForm,
} from './productFormTypes';

type Props = {
  form: ProductForm;
  isTextile: boolean;
  isElectronics: boolean;
  isIndustrial: boolean;
  categoryName: string;
  completeness: number;
  completenessChecks: DppCompletenessCheck[];
  validationErrors: Partial<Record<string, string>>;
  careSymbolCategories: CareSymbolCategory[];
  existingRepairManualPath: string | null;
  existingRepairManualSignedUrl: string | null;
  removeExistingRepairManual: boolean;
  existingDisassemblyGuidePath: string | null;
  existingDisassemblyGuideSignedUrl: string | null;
  removeExistingDisassemblyGuide: boolean;
  existingDemolitionGuidePath: string | null;
  existingDemolitionGuideSignedUrl: string | null;
  removeExistingDemolitionGuide: boolean;
  existingMicroplasticsDocumentName: string | null;
  existingMicroplasticsDocumentSignedUrl: string | null;
  removeExistingMicroplasticsDocument: boolean;
  updateForm: UpdateProductForm;
  onRemoveExistingRepairManual: () => void | Promise<void>;
  onRepairManualFileChange: (file: File | null) => void | Promise<void>;
  onRemoveExistingDisassemblyGuide: () => void | Promise<void>;
  onDisassemblyGuideFileChange: (file: File | null) => void | Promise<void>;
  onRemoveExistingDemolitionGuide: () => void | Promise<void>;
  onDemolitionGuideFileChange: (file: File | null) => void | Promise<void>;
  onRemoveExistingMicroplasticsDocument: () => void | Promise<void>;
  onMicroplasticsReportFileChange: (file: File | null) => void | Promise<void>;
};

export function AddProductStepCircularity({
  form,
  isTextile,
  isElectronics,
  isIndustrial,
  categoryName,
  completeness,
  completenessChecks,
  validationErrors,
  careSymbolCategories,
  existingRepairManualPath,
  existingRepairManualSignedUrl,
  removeExistingRepairManual,
  existingDisassemblyGuidePath,
  existingDisassemblyGuideSignedUrl,
  removeExistingDisassemblyGuide,
  existingDemolitionGuidePath,
  existingDemolitionGuideSignedUrl,
  removeExistingDemolitionGuide,
  existingMicroplasticsDocumentName,
  existingMicroplasticsDocumentSignedUrl,
  removeExistingMicroplasticsDocument,
  updateForm,
  onRemoveExistingRepairManual,
  onRepairManualFileChange,
  onRemoveExistingDisassemblyGuide,
  onDisassemblyGuideFileChange,
  onRemoveExistingDemolitionGuide,
  onDemolitionGuideFileChange,
  onRemoveExistingMicroplasticsDocument,
  onMicroplasticsReportFileChange,
}: Props) {
  const incompleteChecks = completenessChecks.filter(
    (check) => !check.complete,
  );
  const completeChecks = completenessChecks.filter((check) => check.complete);
  const isBattery = form.categoryCode === 'BATTERIES_STORAGE';
  const isConstruction = form.categoryCode === 'CONSTRUCTION_PRODUCTS';
  const isFurniture = form.categoryCode === 'FURNITURE_MATTRESSES';
  const isIronSteel = form.categoryCode === 'IRON_STEEL_METALS';
  const isTiresAutomotive = form.categoryCode === 'TIRES_AUTOMOTIVE';
  const isChemicals = form.categoryCode === 'CHEMICALS';
  const requireRepairManual = isBattery || isElectronics;
  const requireDisassemblyGuide = isBattery || isElectronics || isFurniture;
  const validationMessages = Object.values(validationErrors).filter(Boolean);
  const shortenFileName = (value: string, maxLength = 28) => {
    if (value.length <= maxLength) return value;

    const extensionIndex = value.lastIndexOf('.');
    const hasExtension =
      extensionIndex > 0 && extensionIndex >= value.length - 8;
    const extension = hasExtension ? value.slice(extensionIndex) : '';
    const baseName = hasExtension ? value.slice(0, extensionIndex) : value;
    const availableBaseLength = Math.max(8, maxLength - extension.length - 1);

    return `${baseName.slice(0, availableBaseLength)}...${extension}`;
  };

  const existingRepairManualName = existingRepairManualPath
    ? shortenFileName(
        existingRepairManualPath.split('/').pop() || 'Uploaded file',
      )
    : null;
  const existingDisassemblyGuideName = existingDisassemblyGuidePath
    ? shortenFileName(
        existingDisassemblyGuidePath.split('/').pop() || 'Uploaded file',
      )
    : null;
  const existingDemolitionGuideName = existingDemolitionGuidePath
    ? shortenFileName(
        existingDemolitionGuidePath.split('/').pop() || 'Uploaded file',
      )
    : null;
  const showExistingMicroplasticsDocument = Boolean(
    existingMicroplasticsDocumentName && !removeExistingMicroplasticsDocument,
  );
  const microplasticsDocumentDisplayName =
    existingMicroplasticsDocumentName ?? 'Uploaded file';
  const renderHeaderWithTooltip = (
    label: string,
    tooltipId: string,
    tooltipText: string,
    ariaLabel: string,
  ) => (
    <div className="d-inline-flex align-items-center gap-1">
      <span>{label}</span>
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id={tooltipId}>{tooltipText}</Tooltip>}
      >
        <button
          type="button"
          className="btn btn-link text-muted p-0 border-0 lh-1"
          aria-label={ariaLabel}
        >
          <i className="bi bi-info-circle-fill" />
        </button>
      </OverlayTrigger>
    </div>
  );

  const renderGuideUploader = ({
    label,
    required = false,
    existingPath,
    existingSignedUrl,
    existingName,
    removeExisting,
    selectedFile,
    onFileChange,
    onRemove,
    errorMessage,
    accept = '.pdf,.doc,.docx',
  }: {
    label: string;
    required?: boolean;
    existingPath: string | null;
    existingSignedUrl: string | null;
    existingName: string | null;
    removeExisting: boolean;
    selectedFile: File | null;
    onFileChange: (file: File | null) => void | Promise<void>;
    onRemove: () => void | Promise<void>;
    errorMessage?: string;
    accept?: string;
  }) => {
    const showExistingFile = Boolean(existingPath && !removeExisting);

    return (
      <Col md={6}>
        <Form.Group>
          <Form.Label>
            {label}
            {required ? ' *' : ''}
          </Form.Label>
          {showExistingFile && (
            <div className="small mb-2">
              <div className="text-muted mb-1">Current uploaded file</div>
              <div className="d-flex flex-wrap align-items-center gap-2">
                {existingSignedUrl ? (
                  <a href={existingSignedUrl} target="_blank" rel="noreferrer">
                    {existingName}
                  </a>
                ) : (
                  <span>{existingName}</span>
                )}
                <Button
                  size="sm"
                  variant="link text-danger p-0"
                  onClick={() => void onRemove()}
                >
                  <i className="bi bi-x-circle-fill"></i>
                </Button>
              </div>
            </div>
          )}
          {!showExistingFile && (
            <>
              <Form.Control
                type="file"
                accept={accept}
                isInvalid={Boolean(errorMessage)}
                onChange={(event) =>
                  void onFileChange(
                    (event.target as HTMLInputElement).files?.[0] ?? null,
                  )
                }
              />
              <Form.Control.Feedback type="invalid">
                {errorMessage}
              </Form.Control.Feedback>
              {selectedFile && (
                <div className="small mt-2 d-flex flex-wrap align-items-center gap-2">
                  <span className="text-muted">
                    Selected: {shortenFileName(selectedFile.name)}
                  </span>
                  <Button
                    size="sm"
                    variant="link text-danger p-0"
                    onClick={() => void onFileChange(null)}
                  >
                    <i className="bi bi-x-circle-fill"></i>
                  </Button>
                </div>
              )}
              {!selectedFile && (
                <div className="text-muted small mt-2">
                  Upload a file to attach it to this product.
                </div>
              )}
            </>
          )}
        </Form.Group>
      </Col>
    );
  };

  return (
    <Row className="g-4">
      {validationMessages.length > 0 && (
        <Col xs={12}>
          <Alert variant="danger">
            <ul className="mb-0 ps-3">
              {validationMessages.map((message) => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          </Alert>
        </Col>
      )}

      <Col lg={8}>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Product Carbon Footprint</Form.Label>
              <Form.Control
                type="number"
                min="0"
                step="0.01"
                value={form.carbonFootprint}
                onChange={(event) =>
                  updateForm('carbonFootprint', event.target.value)
                }
              />
              <Form.Text>kg CO2e</Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Expected Product Lifespan *</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="number"
                  min="0"
                  isInvalid={Boolean(validationErrors.lifespan)}
                  value={form.lifespan}
                  onChange={(event) =>
                    updateForm('lifespan', event.target.value)
                  }
                />
                <Form.Select
                  value={form.lifespanUnit}
                  onChange={(event) =>
                    updateForm(
                      'lifespanUnit',
                      event.target.value as ProductForm['lifespanUnit'],
                    )
                  }
                >
                  <option value="years">Years</option>
                  <option value="months">Months</option>
                </Form.Select>
              </div>
              <Form.Control.Feedback type="invalid">
                {validationErrors.lifespan}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>
                {isElectronics
                  ? 'Repairability Index (1-10) *'
                  : 'Repairability Index Score *'}
              </Form.Label>
              <Form.Control
                type="number"
                min="0"
                max="10"
                step="0.1"
                isInvalid={Boolean(validationErrors.repairabilityScore)}
                value={form.repairabilityScore}
                onChange={(event) =>
                  updateForm('repairabilityScore', event.target.value)
                }
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.repairabilityScore}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col xs={12} className="border rounded bg-light p-3">
            <Row className="g-3">
              {renderGuideUploader({
                label: isIndustrial
                  ? 'Refurbishment / Maintenance Manual'
                  : 'Repair Manual',
                required: requireRepairManual,
                existingPath: existingRepairManualPath,
                existingSignedUrl: existingRepairManualSignedUrl,
                existingName: existingRepairManualName,
                removeExisting: removeExistingRepairManual,
                selectedFile: form.repairManualFile,
                onFileChange: onRepairManualFileChange,
                onRemove: onRemoveExistingRepairManual,
                errorMessage: validationErrors.repairManualFile,
              })}
              {renderGuideUploader({
                label: isElectronics
                  ? 'Disassembly Instructions PDF'
                  : 'Disassembly Guide',
                required: requireDisassemblyGuide,
                existingPath: existingDisassemblyGuidePath,
                existingSignedUrl: existingDisassemblyGuideSignedUrl,
                existingName: existingDisassemblyGuideName,
                removeExisting: removeExistingDisassemblyGuide,
                selectedFile: form.disassemblyGuideFile,
                onFileChange: onDisassemblyGuideFileChange,
                onRemove: onRemoveExistingDisassemblyGuide,
                errorMessage: validationErrors.disassemblyGuideFile,
              })}
            </Row>
          </Col>
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Recycling & End-of-Life Instructions *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                isInvalid={Boolean(validationErrors.recyclingInstructions)}
                value={form.recyclingInstructions}
                onChange={(event) =>
                  updateForm('recyclingInstructions', event.target.value)
                }
              />
              <Form.Control.Feedback type="invalid">
                {validationErrors.recyclingInstructions}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>
          {isConstruction && (
            <Col xs={12}>
              <div className="border rounded bg-light p-3">
                <h6 className="mb-3">Construction Circularity</h6>
                <Row className="g-3">
                  {renderGuideUploader({
                    label: 'Demolition Guide PDF',
                    existingPath: existingDemolitionGuidePath,
                    existingSignedUrl: existingDemolitionGuideSignedUrl,
                    existingName: existingDemolitionGuideName,
                    removeExisting: removeExistingDemolitionGuide,
                    selectedFile: form.demolitionGuideFile,
                    onFileChange: onDemolitionGuideFileChange,
                    onRemove: onRemoveExistingDemolitionGuide,
                    errorMessage: validationErrors.demolitionGuideFile,
                    accept: '.pdf,application/pdf',
                  })}
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Demolition Guide URL</Form.Label>
                      <Form.Control
                        value={form.demolitionGuideUrl}
                        isInvalid={Boolean(validationErrors.demolitionGuideUrl)}
                        placeholder="https://example.com/demolition-guide.pdf"
                        onChange={(event) =>
                          updateForm('demolitionGuideUrl', event.target.value)
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.demolitionGuideUrl}
                      </Form.Control.Feedback>
                      <Form.Text className="text-muted small">
                        Provide an external guide URL or upload a PDF.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Col>
          )}
          {isFurniture && (
            <Col xs={12}>
              <div className="border rounded bg-light p-3">
                <h6 className="mb-3">Furniture Circularity</h6>
                <Form.Check
                  type="switch"
                  id="hasRemovableCovers"
                  label="Has removable covers"
                  checked={form.hasRemovableCovers}
                  onChange={(event) =>
                    updateForm('hasRemovableCovers', event.target.checked)
                  }
                />
              </div>
            </Col>
          )}
          {isIronSteel && (
            <Col xs={12}>
              <div className="border rounded bg-light p-3">
                <h6 className="mb-3">Metals Production Data</h6>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Production Route</Form.Label>
                      <Form.Select
                        value={form.productionRoute}
                        onChange={(event) =>
                          updateForm(
                            'productionRoute',
                            event.target
                              .value as ProductForm['productionRoute'],
                          )
                        }
                      >
                        <option value="">Select production route</option>
                        <option value="EAF_ELECTRIC_ARC">
                          Electric Arc Furnace (EAF)
                        </option>
                        <option value="BF_BOF_BLAST_FURNACE">
                          Blast Furnace / Basic Oxygen Furnace (BF/BOF)
                        </option>
                        <option value="RECYCLED_SECONDARY">
                          Recycled / Secondary
                        </option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>CBAM Embedded Emissions</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        isInvalid={Boolean(
                          validationErrors.cbamEmbeddedEmissions,
                        )}
                        value={form.cbamEmbeddedEmissions}
                        onChange={(event) =>
                          updateForm(
                            'cbamEmbeddedEmissions',
                            event.target.value,
                          )
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.cbamEmbeddedEmissions}
                      </Form.Control.Feedback>
                      <Form.Text>kg CO2e / tonne</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            </Col>
          )}
          {isTiresAutomotive && (
            <Col xs={12}>
              <div className="border rounded bg-light p-3">
                <h6 className="mb-3">Tire & Vehicle Circularity</h6>
                <Row className="g-3">
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Rolling Resistance Class</Form.Label>
                      <Form.Select
                        value={form.rollingResistanceClass}
                        onChange={(event) =>
                          updateForm(
                            'rollingResistanceClass',
                            event.target
                              .value as ProductForm['rollingResistanceClass'],
                          )
                        }
                      >
                        <option value="">Select class</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                        <option value="E">E</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Wet Grip Class</Form.Label>
                      <Form.Select
                        value={form.wetGripClass}
                        onChange={(event) =>
                          updateForm(
                            'wetGripClass',
                            event.target.value as ProductForm['wetGripClass'],
                          )
                        }
                      >
                        <option value="">Select class</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                        <option value="D">D</option>
                        <option value="E">E</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>Abrasion Rate</Form.Label>
                      <Form.Control
                        type="number"
                        min="0"
                        step="0.01"
                        isInvalid={Boolean(validationErrors.abrasionRateMgKm)}
                        value={form.abrasionRateMgKm}
                        onChange={(event) =>
                          updateForm('abrasionRateMgKm', event.target.value)
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {validationErrors.abrasionRateMgKm}
                      </Form.Control.Feedback>
                      <Form.Text>mg/km</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Form.Check
                      type="switch"
                      id="isRetreadable"
                      label="Retreadable"
                      checked={form.isRetreadable}
                      onChange={(event) =>
                        updateForm('isRetreadable', event.target.checked)
                      }
                    />
                  </Col>
                </Row>
              </div>
            </Col>
          )}
          {isChemicals && (
            <Col xs={12}>
              <div className="border rounded bg-light p-3">
                <h6 className="mb-3">Chemical Circularity</h6>
                <Form.Group>
                  <Form.Label>
                    Surfactant Biodegradability Percentage
                  </Form.Label>
                  <div className="d-flex align-items-center gap-1">
                    <Form.Control
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      isInvalid={Boolean(
                        validationErrors.surfactantBiodegradabilityPercentage,
                      )}
                      value={form.surfactantBiodegradabilityPercentage}
                      onChange={(event) => {
                        const { value } = event.target;

                        if (!value) {
                          updateForm(
                            'surfactantBiodegradabilityPercentage',
                            value,
                          );
                          return;
                        }

                        const numericValue = Number(value);
                        updateForm(
                          'surfactantBiodegradabilityPercentage',
                          Number.isFinite(numericValue)
                            ? String(Math.min(100, numericValue))
                            : value,
                        );
                      }}
                    />
                    <span style={{ whiteSpace: 'nowrap' }}>%</span>
                  </div>
                  <Form.Control.Feedback type="invalid">
                    {validationErrors.surfactantBiodegradabilityPercentage}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </Col>
          )}
          {isBattery && (
            <>
              <Col xs={12}>
                <div className="border rounded bg-light p-3">
                  <h6 className="mb-3">Battery Electrical Characteristics</h6>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Rated Capacity (Ah) *</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          isInvalid={Boolean(validationErrors.ratedCapacityAh)}
                          value={form.ratedCapacityAh}
                          onChange={(event) =>
                            updateForm('ratedCapacityAh', event.target.value)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.ratedCapacityAh}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Rated Energy (kWh) *</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          isInvalid={Boolean(validationErrors.ratedEnergyKwh)}
                          value={form.ratedEnergyKwh}
                          onChange={(event) =>
                            updateForm('ratedEnergyKwh', event.target.value)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.ratedEnergyKwh}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Nominal Voltage (V) *</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          isInvalid={Boolean(validationErrors.nominalVoltageV)}
                          value={form.nominalVoltageV}
                          onChange={(event) =>
                            updateForm('nominalVoltageV', event.target.value)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.nominalVoltageV}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Minimum Voltage (V) *</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          isInvalid={Boolean(validationErrors.minimumVoltageV)}
                          value={form.minimumVoltageV}
                          onChange={(event) =>
                            updateForm('minimumVoltageV', event.target.value)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.minimumVoltageV}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Maximum Voltage (V) *</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          isInvalid={Boolean(validationErrors.maximumVoltageV)}
                          value={form.maximumVoltageV}
                          onChange={(event) =>
                            updateForm('maximumVoltageV', event.target.value)
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.maximumVoltageV}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          C-rate / Power Capability Limits *
                        </Form.Label>
                        <Form.Control
                          isInvalid={Boolean(
                            validationErrors.powerCapabilityLimits,
                          )}
                          value={form.powerCapabilityLimits}
                          placeholder="e.g. 2C continuous / 150 kW peak"
                          onChange={(event) =>
                            updateForm(
                              'powerCapabilityLimits',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.powerCapabilityLimits}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col xs={12}>
                <div className="border rounded bg-light p-3">
                  <h6 className="mb-3">Battery Efficiency & Durability</h6>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          Round-trip Energy Efficiency (%) *
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          isInvalid={Boolean(
                            validationErrors.roundTripEfficiencyPercent,
                          )}
                          value={form.roundTripEfficiencyPercent}
                          onChange={(event) =>
                            updateForm(
                              'roundTripEfficiencyPercent',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.roundTripEfficiencyPercent}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>
                          Expected Cycle Lifetime (cycles @ DoD % / standard) *
                        </Form.Label>
                        <Form.Control
                          isInvalid={Boolean(
                            validationErrors.cycleLifetimeDescriptor,
                          )}
                          value={form.cycleLifetimeDescriptor}
                          placeholder="e.g. 6,000 cycles @ 80% DoD, IEC 62620"
                          onChange={(event) =>
                            updateForm(
                              'cycleLifetimeDescriptor',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.cycleLifetimeDescriptor}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>
                          Operating Temperature Window (charge / discharge) *
                        </Form.Label>
                        <Form.Control
                          isInvalid={Boolean(
                            validationErrors.operatingTemperatureWindow,
                          )}
                          value={form.operatingTemperatureWindow}
                          placeholder="e.g. Charge: 0 to 45 C, Discharge: -20 to 55 C"
                          onChange={(event) =>
                            updateForm(
                              'operatingTemperatureWindow',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.operatingTemperatureWindow}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col xs={12}>
                <div className="border rounded bg-light p-3">
                  <h6 className="mb-3">Battery Fire Safety & Handling</h6>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>
                          Recommended Extinguishing Agents *
                        </Form.Label>
                        <Form.Control
                          isInvalid={Boolean(
                            validationErrors.extinguishingAgents,
                          )}
                          value={form.extinguishingAgents}
                          placeholder="e.g. Class D media, water deluge, manufacturer-approved battery fire suppressant"
                          onChange={(event) =>
                            updateForm(
                              'extinguishingAgents',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.extinguishingAgents}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Safety Handling Measures *</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          isInvalid={Boolean(
                            validationErrors.safetyHandlingMeasures,
                          )}
                          placeholder="Describe isolation, transport, thermal event response, PPE, storage, and handling precautions."
                          value={form.safetyHandlingMeasures}
                          onChange={(event) =>
                            updateForm(
                              'safetyHandlingMeasures',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.safetyHandlingMeasures}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>
            </>
          )}
          {isTextile && (
            <>
              <Col xs={12}>
                <div className="border rounded bg-light p-3">
                  <h6 className="mb-3">Environmental Footprint</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Water Consumption (PEF)</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.waterConsumption}
                          onChange={(event) =>
                            updateForm('waterConsumption', event.target.value)
                          }
                        />
                        <Form.Text>Liters</Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col xs={12}>
                <div className="border rounded bg-light p-3">
                  <h6 className="mb-3">Durability & Care</h6>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>ISO 3758 Care Symbols *</Form.Label>
                        <div className="d-flex flex-column gap-2 mt-1">
                          {careSymbolCategories.map((cat) => {
                            const catValues = cat.options.map(
                              (option) => option.value,
                            );
                            return (
                              <div key={cat.category}>
                                <div
                                  className="text-muted"
                                  style={{
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    marginBottom: '0.3rem',
                                  }}
                                >
                                  {cat.category}
                                </div>
                                <div className="d-flex flex-wrap gap-1">
                                  {cat.options.map((option) => {
                                    const selected = form.careSymbols.includes(
                                      option.value,
                                    );
                                    return (
                                      <button
                                        key={option.value}
                                        type="button"
                                        title={option.label}
                                        className={`btn btn-sm ${selected ? 'btn-primary' : 'btn-outline-secondary'}`}
                                        style={{
                                          width: 44,
                                          height: 44,
                                          padding: 6,
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                        }}
                                        onClick={() => {
                                          const withoutCategory =
                                            form.careSymbols.filter(
                                              (symbol) =>
                                                !catValues.includes(symbol),
                                            );
                                          updateForm(
                                            'careSymbols',
                                            selected
                                              ? withoutCategory
                                              : [
                                                  ...withoutCategory,
                                                  option.value,
                                                ],
                                          );
                                        }}
                                      >
                                        {option.icon}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {validationErrors.careSymbols && (
                          <div className="text-danger small mt-2">
                            {validationErrors.careSymbols}
                          </div>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Color Fastness Rating (1–5)</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="5"
                          step="0.1"
                          value={form.colorFastnessRating}
                          onChange={(event) =>
                            updateForm(
                              'colorFastnessRating',
                              event.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Pilling Resistance Rating (1–5)</Form.Label>
                        <Form.Control
                          type="number"
                          min="1"
                          max="5"
                          step="0.1"
                          value={form.pillingResistanceRating}
                          onChange={(event) =>
                            updateForm(
                              'pillingResistanceRating',
                              event.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col xs={12}>
                <div className="border rounded bg-light p-3">
                  <h6 className="mb-3">Repair & End-of-Life</h6>
                  <Row className="g-3">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Spare Parts Availability</Form.Label>
                        <Form.Control
                          value={form.sparePartsAvailability}
                          placeholder="e.g. Extra buttons included, Replacement zippers available online"
                          onChange={(event) =>
                            updateForm(
                              'sparePartsAvailability',
                              event.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Take-Back / Collection Scheme</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          placeholder="Describe how the consumer can return the garment for recycling..."
                          value={form.takeBackScheme}
                          onChange={(event) =>
                            updateForm('takeBackScheme', event.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col xs={12}>
                <div className="border rounded bg-light p-3">
                  <h6 className="mb-3">Compliance & Chemical Shedding</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          Microplastics Release Declaration *
                        </Form.Label>
                        <Form.Select
                          value={form.microplasticsStatus}
                          isInvalid={Boolean(
                            validationErrors.microplasticsStatus,
                          )}
                          onChange={(event) => {
                            const value = event.target
                              .value as ProductForm['microplasticsStatus'];
                            updateForm('microplasticsStatus', value);
                            if (value !== 'tested') {
                              updateForm('microplasticsReportFile', null);
                            }
                          }}
                        >
                          <option value="">— Select —</option>
                          <option value="not_applicable">
                            Not Applicable (100% Natural Fibers)
                          </option>
                          <option value="untested">
                            Untested for synthetic shedding
                          </option>
                          <option value="tested">
                            Tested (Attach shedding report)
                          </option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.microplasticsStatus}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>EPR Registration Number</Form.Label>
                        <Form.Control
                          value={form.eprRegistrationNumber}
                          placeholder="EU EPR registration number"
                          onChange={(event) =>
                            updateForm(
                              'eprRegistrationNumber',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Text className="text-muted">
                          Required for EU market surveillance
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    {form.microplasticsStatus === 'tested' && (
                      <Col xs={12}>
                        <Form.Group>
                          <Form.Label>
                            Microplastics Shedding Report *
                          </Form.Label>
                          {showExistingMicroplasticsDocument ? (
                            <div className="small">
                              <div className="text-muted mb-1">
                                Current uploaded file
                              </div>
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                {existingMicroplasticsDocumentSignedUrl ? (
                                  <a
                                    href={
                                      existingMicroplasticsDocumentSignedUrl
                                    }
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    {shortenFileName(
                                      microplasticsDocumentDisplayName,
                                    )}
                                  </a>
                                ) : (
                                  <span>
                                    {shortenFileName(
                                      microplasticsDocumentDisplayName,
                                    )}
                                  </span>
                                )}
                                <Button
                                  size="sm"
                                  variant="link text-danger p-0"
                                  onClick={() =>
                                    void onRemoveExistingMicroplasticsDocument()
                                  }
                                >
                                  <i className="bi bi-x-circle-fill"></i>
                                </Button>
                              </div>
                            </div>
                          ) : form.microplasticsReportFile ? (
                            <div className="small">
                              <div className="text-muted mb-1">
                                Current uploaded file
                              </div>
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                <span className="text-muted">
                                  {shortenFileName(
                                    form.microplasticsReportFile.name,
                                  )}
                                </span>
                                <Button
                                  size="sm"
                                  variant="link text-danger p-0"
                                  onClick={() =>
                                    void onMicroplasticsReportFileChange(null)
                                  }
                                >
                                  <i className="bi bi-x-circle-fill"></i>
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <Form.Control
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                onChange={(event) =>
                                  void onMicroplasticsReportFileChange(
                                    (event.target as HTMLInputElement)
                                      .files?.[0] ?? null,
                                  )
                                }
                              />
                              <div className="text-muted small mt-2">
                                Upload a file to attach it to this product.
                              </div>
                            </>
                          )}
                        </Form.Group>
                      </Col>
                    )}
                  </Row>
                </div>
              </Col>
            </>
          )}
          {isElectronics && (
            <Col md={6}>
              <Form.Group>
                <Form.Label>Software Support Expiration Date</Form.Label>
                <Form.Control
                  type="date"
                  value={form.softwareSupportExpiry}
                  onChange={(event) =>
                    updateForm('softwareSupportExpiry', event.target.value)
                  }
                />
              </Form.Group>
            </Col>
          )}
          {isIndustrial && (
            <>
              <Col xs={12}>
                <div className="border rounded bg-light p-3">
                  <h6 className="mb-3">Spare Parts & Service Guarantees</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          {renderHeaderWithTooltip(
                            'Spare Parts Availability Years *',
                            'spare-parts-availability-years-tooltip',
                            'Guaranteed years of spare parts availability post-production.',
                            'Show spare parts availability years guidance',
                          )}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          isInvalid={Boolean(
                            validationErrors.sparePartsAvailabilityYears,
                          )}
                          value={form.sparePartsAvailabilityYears}
                          placeholder="e.g. 10"
                          onChange={(event) =>
                            updateForm(
                              'sparePartsAvailabilityYears',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.sparePartsAvailabilityYears}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Spare Parts Catalog URL</Form.Label>
                        <Form.Control
                          type="url"
                          isInvalid={Boolean(
                            validationErrors.sparePartsCatalogUrl,
                          )}
                          value={form.sparePartsCatalogUrl}
                          placeholder="https://example.com/spare-parts"
                          onChange={(event) =>
                            updateForm(
                              'sparePartsCatalogUrl',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.sparePartsCatalogUrl}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>

              <Col xs={12}>
                <div className="border rounded bg-light p-3">
                  <h6 className="mb-3">Software & Durability</h6>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>
                          {renderHeaderWithTooltip(
                            'Software Support Years *',
                            'software-support-years-tooltip',
                            'Guaranteed years of security/firmware updates.',
                            'Show software support years guidance',
                          )}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="1"
                          isInvalid={Boolean(
                            validationErrors.softwareSupportYears,
                          )}
                          value={form.softwareSupportYears}
                          placeholder="e.g. 10"
                          onChange={(event) =>
                            updateForm(
                              'softwareSupportYears',
                              event.target.value,
                            )
                          }
                        />
                        <Form.Control.Feedback type="invalid">
                          {validationErrors.softwareSupportYears}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Energy Efficiency Rating</Form.Label>
                        <Form.Control
                          value={form.energyEfficiencyRating}
                          placeholder="e.g. IE4"
                          onChange={(event) =>
                            updateForm(
                              'energyEfficiencyRating',
                              event.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </div>
              </Col>
            </>
          )}
        </Row>
      </Col>
      <Col lg={4}>
        <div
          className="border rounded bg-light p-3 sticky-lg-top"
          style={{ top: '1rem' }}
        >
          <div className="d-flex align-items-center justify-content-between mb-2">
            <div className="fw-semibold">DPP Completeness</div>
            <Badge
              bg={completeness === 100 ? 'success' : 'warning'}
              text={completeness === 100 ? undefined : 'dark'}
            >
              {completeness}%
            </Badge>
          </div>
          <ProgressBar
            now={completeness}
            variant={completeness === 100 ? 'success' : 'warning'}
            className="mb-3"
          />
          <div className="small text-muted">
            {categoryName || 'Category not selected'}
          </div>
          {incompleteChecks.length > 0 ? (
            <>
              <div className="small fw-semibold text-warning mt-3 mb-2">
                Missing for publication
              </div>
              <ul className="small mb-0 ps-3">
                {incompleteChecks.map((check) => (
                  <li key={check.key} className="text-warning">
                    {check.label}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <Alert variant="success" className="py-2 px-3 mt-3 mb-0 small">
              All required checklist items are complete for this category.
            </Alert>
          )}
          {completeChecks.length > 0 && (
            <>
              <div className="small fw-semibold text-success mt-3 mb-2">
                Completed
              </div>
              <ul className="small mb-0 ps-3">
                {completeChecks.map((check) => (
                  <li key={check.key} className="text-success">
                    {check.label}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </Col>
    </Row>
  );
}
